import type { ImageScenario } from '../game/imageTypes';
import type { PartnerId, ToolId } from '../game/partners';
import { allTools, toolById } from '../game/partners.ts';
import { mergeLevels } from '../game/contentStore.ts';
// Explicit .ts extension so Node's native type stripping can load this file
// directly in build-time tools; Vite resolves it identically.
import { boardConfig } from './boardConfig.ts';
import { workItem } from './workItem.ts';
import { workflowTransition } from './workflowTransition.ts';
import { permissions } from './permissions.ts';
import { sprintBoard } from './sprintBoard.ts';
import { bugReport } from './bugReport.ts';

/**
 * Every level, across every tool.
 *
 * Adding a level is a DATA change only:
 *   1. Drop the screenshot in `public/scenarios/`.
 *   2. Run `npm run measure -- public/scenarios/<file>` to get the arrow
 *      regions, measured from the red pixels rather than guessed.
 *   3. Write a scenario file next to this one and add it to the array below.
 *      Its `source.toolId` decides which tool's set it joins — there is no
 *      second list to keep in step.
 *   4. Run `npm run review` and sign the answers off before it ships.
 *
 * Adding a whole new TOOL is also a data change: add it to `partners.ts` and
 * give it levels. It appears in the picker on its own, and shows as not-yet-
 * available until it has at least one level.
 *
 * Nothing in the renderer or the grader needs to change, ever. If you find
 * yourself editing a component to make a level work, the level is fighting the
 * model and the model should win.
 */
export const LEVELS: ImageScenario[] = [
  boardConfig,
  workItem,
  workflowTransition,
  permissions,
  sprintBoard,
  bugReport,
];

/**
 * Every level a player can actually see: the compiled set with the runtime
 * layer applied.
 *
 * Read this rather than LEVELS anywhere the answer is shown to a person.
 * LEVELS is the baseline that ships in the bundle; admins can add to it and
 * patch it at runtime, and a component reading the raw array would quietly
 * show stale wording.
 */
export function allLevels(): ImageScenario[] {
  return mergeLevels(LEVELS);
}

export function levelById(id: string): ImageScenario | undefined {
  return allLevels().find((level) => level.id === id);
}

/** A tool's levels, in campaign order. */
export function levelsForTool(toolId: ToolId): ImageScenario[] {
  return allLevels().filter((level) => level.source.toolId === toolId);
}

/**
 * How many levels a tool has, against how many it is meant to end up with.
 *
 * Availability is DERIVED from the levels that exist, never declared. A hand-
 * maintained "coming soon" flag is a thing that can be wrong, and being wrong
 * here means either advertising a tool with nothing behind it or hiding one
 * that is ready.
 */
export function toolProgress(toolId: ToolId): {
  ready: number;
  target: number;
  available: boolean;
} {
  const ready = levelsForTool(toolId).length;
  return { ready, target: toolById(toolId).targetLevels, available: ready > 0 };
}

/** Tools a partner makes that have at least one level. */
export function availableToolsForPartner(partnerId: PartnerId): ToolId[] {
  return allTools().filter((t) => t.partnerId === partnerId)
    .filter((t) => toolProgress(t.id).available)
    .map((t) => t.id);
}
